import type { CSSProperties } from 'react';
import type { PayrollPolicy, Shift } from '../../../services/httpServices/hrService';

/** Built-in fallback used by the payroll engine when no policy is configured. */
export const DEFAULT_WEEKLY_OFF_DAYS = [5];

/** Palette offered when creating/editing a shift. */
export const SHIFT_COLORS = [
  '#0f766e',
  '#f59e0b',
  '#6366f1',
  '#22c55e',
  '#3b82f6',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#64748b',
];

export const WEEKDAYS = [
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
  { value: 0, short: 'Sun', label: 'Sunday' },
];

export function getWeekStart(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function formatISODate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Convert an ISO timestamp into a 12-hour label (e.g. "08:00 AM"). */
export function extractTime(isoString?: string) {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

/** "08:00:00" -> "08:00" (value for <input type="time" />). */
export function to24h(time?: string | null) {
  if (!time) return '';
  return time.slice(0, 5);
}

/** "08:00:00" -> "08:00 AM" for compact display. */
export function formatShiftTime(time?: string | null) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function isDateInRange(dateStr: string, startDateStr: string, endDateStr: string) {
  const date = new Date(dateStr).getTime();
  const start = new Date(startDateStr).getTime();
  const end = new Date(endDateStr).getTime();
  return date >= start && date <= end;
}

/** Build a light background + readable text style from a hex colour. */
export function shiftColorStyle(color?: string): CSSProperties {
  const hex = (color || '#0f766e').replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16) || 15;
  const g = parseInt(full.slice(2, 4), 16) || 118;
  const b = parseInt(full.slice(4, 6), 16) || 110;
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)`,
    color: `rgb(${r}, ${g}, ${b})`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.25)`,
  };
}

export function findShift(shifts: Shift[], id?: string | null) {
  if (!id) return undefined;
  return shifts.find((s) => s.id === id);
}

/** Compute the last day of the month for a given date. */
export function endOfMonth(date: Date) {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfMonth(date: Date) {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function employeeName(employee: any) {
  return `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || employee?.name || 'Employee';
}

/**
 * Resolve an employee's paid weekly off days, mirroring the payroll engine:
 * an explicit employee override wins, then their policy, then the department
 * policy, then the active default policy, then the built-in Friday fallback.
 */
export function resolveWeeklyOffDays(
  employee: any,
  policies?: PayrollPolicy[] | null,
): number[] {
  const own = employee?.weeklyOffDays;
  if (Array.isArray(own) && own.length > 0) return own.map(Number);

  const active = (policies || []).filter((policy) => policy.isActive !== false);

  if (employee?.payrollPolicyId) {
    const explicit = active.find((policy) => policy.id === employee.payrollPolicyId);
    if (explicit?.weeklyOffDays?.length) return explicit.weeklyOffDays.map(Number);
  }
  if (employee?.departmentId) {
    const department = active.find((policy) => policy.departmentId === employee.departmentId);
    if (department?.weeklyOffDays?.length) return department.weeklyOffDays.map(Number);
  }
  const fallback = active.find((policy) => policy.isDefault);
  if (fallback?.weeklyOffDays?.length) return fallback.weeklyOffDays.map(Number);

  return DEFAULT_WEEKLY_OFF_DAYS;
}

/** True when the given date falls on one of the employee's weekly off days. */
export function isWeeklyOffDay(date: Date | string, offDays: number[]): boolean {
  if (!Array.isArray(offDays) || offDays.length === 0) return false;
  const parsed =
    typeof date === 'string' ? new Date(`${date.slice(0, 10)}T00:00:00`) : date;
  if (Number.isNaN(parsed.getTime())) return false;
  return offDays.includes(parsed.getDay());
}

/** "Fri, Sat" for the configured off-day numbers. */
export function formatWeeklyOffDays(offDays: number[]): string {
  if (!Array.isArray(offDays) || offDays.length === 0) return '';
  return offDays
    .map((day) => WEEKDAYS.find((weekday) => weekday.value === day)?.short)
    .filter(Boolean)
    .join(', ');
}

/**
 * Derive a shift code from a shift name.
 * "Morning Shift" -> "MORNING_SHIFT", "On-Call / Standby" -> "ON_CALL_STANDBY".
 */
export function generateShiftCode(name: string) {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '')
    .slice(0, 50);
}
